import { appConfig } from "@/config/appConfig";
import { logger } from "@/utils/logger";
import { setCookie, getCookie, deleteCookie } from "@/utils/cookies";

interface OAuthConfig {
  authorizationUrl: string;
  logoutUrl: string;
  tokenUrl: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  userInfoUrl?: string;
}

interface PKCEChallenge {
  codeVerifier: string;
  codeChallenge: string;
}

export interface OAuthTokenResponse {
  access_token: string;
  token_type: string;
  expires_in?: number;
  refresh_token?: string;
  scope?: string;
  id_token?: string;
}

export interface OAuthUserInfo {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  picture?: string;
  [key: string]: unknown;
}

export function getOAuthConfig(): OAuthConfig {
  const o = appConfig.oauth;

  return {
    authorizationUrl: o.authorizationUrl,
    logoutUrl: o.logoutUrl,
    tokenUrl: o.tokenUrl,
    clientId: o.clientId,
    redirectUri: o.redirectUri,
    scope: o.scope,
    ...(o.userInfoUrl ? { userInfoUrl: o.userInfoUrl } : {}),
  };
}

export function isOAuthEnabled(): boolean {
  return appConfig.oauth.enabled;
}

export function validateOAuthConfig(): boolean {
  try {
    if (!appConfig.oauth.enabled) {
      return true;
    }

    const o = appConfig.oauth;
    const requiredVars: Record<string, string> = {
      VITE_OAUTH_AUTHORIZATION_URL: o.authorizationUrl,
      VITE_OAUTH_TOKEN_URL: o.tokenUrl,
      VITE_OAUTH_CLIENT_ID: o.clientId,
      VITE_OAUTH_REDIRECT_URI: o.redirectUri,
      VITE_OAUTH_SCOPE: o.scope,
      VITE_OAUTH_USERINFO_URL: o.userInfoUrl,
    };

    const missing: string[] = [];

    for (const [key, value] of Object.entries(requiredVars)) {
      if (!value || value === "" || value === "undefined") {
        missing.push(key);
      }
    }

    if (missing.length > 0) {
      console.error(
        `\n⚠️ OAuth Configuration Error: Missing ${missing.length} variable(s):\n` +
          missing.map((v) => `  - ${v}`).join("\n") +
          "\n\nPlease set these variables in:\n" +
          "  - .env.production file (build-time)\n" +
          "  - OR window.__RUNTIME_CONFIG__ (runtime)\n",
      );

      return false;
    }

    return true;
  } catch (error) {
    console.error("❌ Error in validateOAuthConfig:", error);

    return false;
  }
}

function generateRandomString(length: number): string {
  const possible =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const values = crypto.getRandomValues(new Uint8Array(length));

  return Array.from(values)
    .map((x) => possible[x % possible.length])
    .join("");
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);

  return crypto.subtle.digest("SHA-256", data);
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

export async function generatePKCEChallenge(): Promise<PKCEChallenge> {
  const codeVerifier = generateRandomString(128);
  const hashed = await sha256(codeVerifier);
  const codeChallenge = base64UrlEncode(hashed);

  return {
    codeVerifier,
    codeChallenge,
  };
}

export async function buildAuthorizationUrl(state?: string): Promise<string> {
  const config = getOAuthConfig();
  const pkce = await generatePKCEChallenge();

  sessionStorage.setItem("oauth_code_verifier", pkce.codeVerifier);

  if (state) {
    sessionStorage.setItem("oauth_state", state);
  }

  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    code_challenge: pkce.codeChallenge,
    code_challenge_method: "S256",
  });

  if (state) {
    params.append("state", state);
  }

  return `${config.authorizationUrl}?${params.toString()}`;
}


export function buildLogoutUrl(returnUrl: string): string {
  const config = getOAuthConfig();

  const params = new URLSearchParams({
    post_logout_redirect_uri: returnUrl,
    id_token_hint: getCookie('oauth_id_token') || '',
  });

  return `${config.logoutUrl}?${params.toString()}`;
}

export async function exchangeCodeForToken(
  code: string,
): Promise<OAuthTokenResponse> {
  const config = getOAuthConfig();
  const codeVerifier = sessionStorage.getItem("oauth_code_verifier");

  if (!codeVerifier) {
    throw new Error(
      "Code verifier not found. OAuth flow may have been interrupted.",
    );
  }

  const params = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: config.redirectUri,
    client_id: config.clientId,
    code_verifier: codeVerifier,
  });

  try {
    const response = await fetch(config.tokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const errorText = await response.text();

      logger.error("Token exchange failed:", errorText);
      throw new Error(
        `Token exchange failed: ${response.status} ${response.statusText}`,
      );
    }

    const data = (await response.json()) as OAuthTokenResponse;

    sessionStorage.removeItem("oauth_code_verifier");
    sessionStorage.removeItem("oauth_state");

    return data;
  } catch (error) {
    logger.error("Error exchanging code for token:", error);
    throw error;
  }
}

export async function refreshAccessToken(
  refreshToken: string,
): Promise<OAuthTokenResponse> {
  const config = getOAuthConfig();

  const params = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: config.clientId,
  });

  try {
    const response = await fetch(config.tokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const errorText = await response.text();

      logger.error("Token refresh failed:", errorText);
      throw new Error(
        `Token refresh failed: ${response.status} ${response.statusText}`,
      );
    }

    return (await response.json()) as OAuthTokenResponse;
  } catch (error) {
    logger.error("Error refreshing token:", error);
    throw error;
  }
}

export async function initiateOAuthLogin(returnUrl?: string): Promise<void> {
  if (!isOAuthEnabled()) {
    throw new Error("OAuth is not enabled");
  }

  const state = generateRandomString(32);

  if (returnUrl) {
    sessionStorage.setItem("oauth_return_url", returnUrl);
  }

  const authUrl = await buildAuthorizationUrl(state);

  window.location.href = authUrl;
}

export async function initiateOAuthLogout(returnUrl: string): Promise<void> {
  if (!isOAuthEnabled()) {
    throw new Error("OAuth is not enabled");
  }

  const authUrl = buildLogoutUrl(returnUrl);

  window.location.href = authUrl;
}

export async function handleOAuthCallback(
  urlParams: URLSearchParams,
): Promise<OAuthTokenResponse> {
  const code = urlParams.get("code");
  const state = urlParams.get("state");
  const error = urlParams.get("error");
  const errorDescription = urlParams.get("error_description");

  if (error) {
    throw new Error(errorDescription || error);
  }

  if (!code) {
    throw new Error("Authorization code not found in callback URL");
  }

  const storedState = sessionStorage.getItem("oauth_state");

  if (!storedState) {
    throw new Error(
      "OAuth state not found. Flow may have been interrupted or tampered with.",
    );
  }

  if (state !== storedState) {
    throw new Error("State mismatch. Possible CSRF attack.");
  }

  return await exchangeCodeForToken(code);
}

export function getOAuthReturnUrl(): string | null {
  const returnUrl = sessionStorage.getItem("oauth_return_url");

  if (returnUrl) {
    sessionStorage.removeItem("oauth_return_url");
  }

  return returnUrl;
}

export function storeOAuthTokens(tokens: OAuthTokenResponse): void {
  const maxAge = tokens.expires_in || 3600;

  setCookie("oauth_access_token", tokens.access_token, {
    maxAge,
    secure: import.meta.env.PROD,
    sameSite: "Strict",
  });

  if (tokens.refresh_token) {
    setCookie("oauth_refresh_token", tokens.refresh_token, {
      maxAge: 30 * 24 * 60 * 60, // 30 days
      secure: import.meta.env.PROD,
      sameSite: "Strict",
    });
  }

  if (tokens.id_token) {
    setCookie("oauth_id_token", tokens.id_token, {
      maxAge,
      secure: import.meta.env.PROD,
      sameSite: "Strict",
    });
  }

  if (tokens.expires_in) {
    const expiresAt = Date.now() + tokens.expires_in * 1000;

    setCookie("oauth_expires_at", expiresAt.toString(), {
      maxAge,
      secure: import.meta.env.PROD,
      sameSite: "Strict",
    });
  }

  logger.debug("[OAuth] Tokens stored in secure cookies");
}

export function getOAuthAccessToken(): string | null {
  return getCookie("oauth_access_token");
}

export function getOAuthRefreshToken(): string | null {
  return getCookie("oauth_refresh_token");
}

export function clearOAuthTokens(): void {
  deleteCookie("oauth_access_token");
  deleteCookie("oauth_refresh_token");
  deleteCookie("oauth_id_token");
  deleteCookie("oauth_expires_at");
  logger.debug("[OAuth] Tokens cleared from cookies");
}

export function isOAuthTokenExpired(): boolean {
  const expiresAt = getCookie("oauth_expires_at");

  if (expiresAt) {
    const expired = Date.now() >= parseInt(expiresAt, 10);

    if (!expired) {
      return false;
    }
  }

  const accessToken = getOAuthAccessToken();

  if (accessToken) {
    const payload = decodeJWT(accessToken);

    if (payload?.exp && typeof payload.exp === "number") {
      const jwtExpired = Date.now() >= payload.exp * 1000;

      if (!jwtExpired) {
        logger.debug("[OAuth] Token valid according to JWT exp claim");

        return false;
      }
    }
  }

  return true;
}

export function decodeJWT(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");

    if (parts.length !== 3) {
      logger.error("Invalid JWT format");

      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));

    return JSON.parse(decoded) as Record<string, unknown>;
  } catch (error) {
    logger.error("Error decoding JWT:", error);

    return null;
  }
}

export async function fetchUserInfo(
  accessToken: string,
): Promise<OAuthUserInfo> {
  const config = getOAuthConfig();

  if (!config.userInfoUrl) {
    throw new Error("User info URL not configured");
  }

  try {
    const response = await fetch(config.userInfoUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();

      logger.error("Failed to fetch user info:", errorText);
      throw new Error(
        `Failed to fetch user info: ${response.status} ${response.statusText}`,
      );
    }

    return (await response.json()) as OAuthUserInfo;
  } catch (error) {
    logger.error("Error fetching user info:", error);
    throw error;
  }
}

export async function getUserInfo(
  tokens: OAuthTokenResponse,
): Promise<OAuthUserInfo> {
  if (tokens.id_token) {
    const idTokenPayload = decodeJWT(tokens.id_token);

    if (idTokenPayload) {
      return idTokenPayload as OAuthUserInfo;
    }
  }

  return await fetchUserInfo(tokens.access_token);
}
